/*
 * community-reviews.js
 *
 * - Modal popup on card click → shows full review body
 * - "Load more" reveals additional cards in chunks of 12
 *
 * Reads review data from the in-page <script type="application/json">
 * blob emitted by the section. Cards carry data-review-id to look up.
 *
 * 2026-05-11 (L39): listens for `pageshow` with `event.persisted === true`
 * (the bfcache-restore signal). When the user navigates away and comes
 * back via the browser back/forward button, Safari/Chrome restore the
 * entire DOM + JS state — which means "Load more" clicks, an open
 * modal, and any scroll position are all preserved. Customers reported
 * "reviews don't reset when navigation — still open all the posts I
 * had open" — i.e. cards 13+ stay visible after a back/forward. The
 * pageshow handler dispatches a `community-reviews:reset` custom event
 * to each section; init() binds a listener that closes the modal,
 * collapses the load-more state back to `initial`, and clears the
 * body scroll-lock that the modal sets.
 */
(function () {
  'use strict';

  // 2026-05-14: review-style A/B/C URL toggle removed. Style B (the
  // 2-col masonry) is now the default mobile layout — baked into
  // community-reviews.css directly. If any user's session set
  // `data-review-style` via the previous toggle, clear it on next
  // page load so the cleaned-up CSS isn't perturbed by stale state.
  try {
    sessionStorage.removeItem('trieye_reviewstyle');
    document.documentElement.removeAttribute('data-review-style');
  } catch (e) { /* silent */ }

  function init(section) {
    if (section.dataset.communityInit === '1') return;
    section.dataset.communityInit = '1';

    var grid = section.querySelector('.community-reviews__grid');
    var dataNode = section.querySelector('script.community-reviews__data');
    if (!grid || !dataNode) return;

    var data;
    try {
      data = JSON.parse(dataNode.textContent || '[]');
    } catch (e) {
      return;
    }
    var byId = {};
    data.forEach(function (r) { byId[String(r.id)] = r; });

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.community-card[data-review-id]'));
    var initial = parseInt(section.dataset.initialCount || '12', 10);
    var increment = 12;

    // 2026-05-14: the earlier "split text-left / photo-right"
    // experiment was rolled back per user request — photo cards
    // now use the FULL mobile width again via CSS Columns
    // `column-span: all`. This removes any prior DOM split if it
    // was applied in a previous session (bfcache restore, etc.)
    // so the layout returns to a flat list under the grid.
    (function unsplitIfPresent() {
      if (grid.dataset.communitySplit !== '1') return;
      var cols = grid.querySelectorAll('.community-reviews__col');
      cols.forEach(function (col) {
        // Move children back to the grid, then drop the col wrapper.
        while (col.firstChild) grid.insertBefore(col.firstChild, col);
        col.parentNode.removeChild(col);
      });
      delete grid.dataset.communitySplit;
    })();

    // ===== Load more =====
    var loadMoreBtn = section.querySelector('.community-reviews__loadmore');
    var visible = initial;

    function applyVisibility() {
      // 2026-05-11 (L40a): media-only mode now PAGINATES photos too.
      // Previously the L19 implementation un-hid every card and let CSS
      // hide text cards — but that meant clicking "Only with photos" on
      // a section with 30+ photo reviews dropped the whole pile in at
      // once. Customer reported "it loads too many picture reviews when
      // clicking on only image reviews." Now we count visible PHOTO
      // cards against the same `visible` cap and only un-hide the first
      // N photo cards. Load more reuses the same counter and increments
      // through the photo set the same way it increments through the
      // mixed set in normal mode.
      var mediaOnly = section.classList.contains('community-reviews--media-only');
      var photoSeen = 0;
      cards.forEach(function (c, i) {
        var isPhoto = c.classList.contains('community-card--photo');
        var inView;
        if (mediaOnly) {
          if (isPhoto) {
            inView = photoSeen < visible;
            photoSeen++;
          } else {
            // Text cards are never shown in photo-only mode; setting
            // `hidden` here matches the CSS rule and keeps DOM state
            // consistent (so bfcache restore + reset works the same
            // way in both modes).
            inView = false;
          }
        } else {
          inView = i < visible;
        }
        if (inView) c.removeAttribute('hidden');
        else c.setAttribute('hidden', '');
      });
      if (loadMoreBtn) {
        // Pagination accounting differs by mode. In photo-only mode the
        // "total" is the count of photo cards, not the full card list,
        // and the "current" is the smaller of `visible` and that total.
        var total, current;
        if (mediaOnly) {
          total = 0;
          cards.forEach(function (c) {
            if (c.classList.contains('community-card--photo')) total++;
          });
          current = visible < total ? visible : total;
        } else {
          total = cards.length;
          current = visible;
        }
        if (current >= total) {
          loadMoreBtn.setAttribute('hidden', '');
          var seeAll = section.querySelector('.community-reviews__see-all');
          if (seeAll) seeAll.removeAttribute('hidden');
        } else {
          loadMoreBtn.removeAttribute('hidden');
          // Hide the see-all link until we've exhausted Load more.
          var seeAllMore = section.querySelector('.community-reviews__see-all');
          if (seeAllMore) seeAllMore.setAttribute('hidden', '');
          // 2026-05-14: keep the label clean — no "(N left)" countdown
          // on the button. Same rationale as the cart-button "Only N
          // left" removal: don't surface inventory/queue counts that
          // create artificial urgency.
          var labelEl = loadMoreBtn.querySelector('[data-loadmore-label]');
          if (labelEl) labelEl.textContent = 'Load more reviews';
        }
      }
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function (e) {
        e.preventDefault();
        visible = Math.min(visible + increment, cards.length);
        applyVisibility();
      });
    }
    applyVisibility();

    // ===== L18: "Only with photos" filter toggle =====
    // The button on the section header flips a class on the section root.
    // CSS handles the actual hiding: .community-reviews--media-only hides
    // .community-card--text. Hero cards stay visible in both states.
    // We persist the choice in localStorage so it survives navigation.
    var mediaToggleBtn = section.querySelector('[data-community-media-toggle]');
    if (mediaToggleBtn) {
      var STORAGE_KEY = 'trieye_community_reviews_media_only';
      var initial = false;
      try { initial = window.localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) {}
      function applyMediaFilter(on) {
        section.classList.toggle('community-reviews--media-only', !!on);
        mediaToggleBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        mediaToggleBtn.classList.toggle('is-active', !!on);
        try { window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch (e) {}
        // L40a: reset Load more pagination on filter toggle so the user
        // always lands on a clean count. Previous behavior carried over
        // however many cards they'd paginated through in the OTHER mode,
        // which compounded with the media-only "drop everything in at
        // once" bug — clicking the filter showed every photo the visitor
        // had previously paginated through. Reading from the dataset
        // (instead of the closure-captured `initial`) sidesteps the
        // var shadowing on line ~110 below.
        visible = parseInt(section.dataset.initialCount || '12', 10);
        // L19: re-evaluate visibility under the new mode.
        applyVisibility();
      }
      mediaToggleBtn.addEventListener('click', function () {
        var pressed = mediaToggleBtn.getAttribute('aria-pressed') === 'true';
        applyMediaFilter(!pressed);
      });
      applyMediaFilter(initial);
    }

    // ===== Modal popup with prev/next navigation =====
    var dialog = section.querySelector('.community-reviews__dialog');
    if (!dialog) return;
    var dialogBody = dialog.querySelector('.community-reviews__dialog-body');
    var prevBtn = dialog.querySelector('.community-reviews__dialog-prev');
    var nextBtn = dialog.querySelector('.community-reviews__dialog-next');

    // Navigation walks the same data array. Index = position in `data`.
    var currentIdx = -1;

    function escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function formatDate(iso) {
      if (!iso) return '';
      var d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }

    function renderReview(idx) {
      var r = data[idx];
      if (!r) return;
      currentIdx = idx;
      var stars = '★'.repeat(r.rating || 5) + '☆'.repeat(5 - (r.rating || 5));
      var verifiedLabel = '';
      if (r.verified === 'verified-purchase' || r.verified === 'confirmed-buyer' || r.verified === 'buyer') {
        verifiedLabel = '<span class="community-dialog__verified">Verified Buyer</span>';
      }
      var pic = r.picture_url ? '<img class="community-dialog__photo" src="' + escapeHtml(r.picture_url) + '" alt="' + escapeHtml(r.reviewer_name || 'Customer photo') + '" loading="eager">' : '';
      var titleHtml = r.title ? '<h3 class="community-dialog__title">' + escapeHtml(r.title) + '</h3>' : '';
      var counter = '<div class="community-dialog__counter">' + (idx + 1) + ' of ' + data.length + '</div>';
      dialogBody.innerHTML =
        counter +
        '<div class="community-dialog__stars" aria-label="' + (r.rating || 5) + ' stars">' + stars + '</div>' +
        titleHtml +
        '<p class="community-dialog__body">' + escapeHtml(r.body || '').replace(/\n/g, '<br>') + '</p>' +
        pic +
        '<div class="community-dialog__attr">' +
          '<span class="community-dialog__name">' + escapeHtml(r.reviewer_name || 'Verified Customer') + '</span>' +
          '<span class="community-dialog__date">' + escapeHtml(formatDate(r.created_at)) + '</span>' +
          verifiedLabel +
        '</div>';
      // Update nav button states
      if (prevBtn) prevBtn.disabled = idx <= 0;
      if (nextBtn) nextBtn.disabled = idx >= data.length - 1;
      // Reset scroll
      if (dialogBody.scrollTop !== undefined) dialogBody.scrollTop = 0;
    }

    function openModal(reviewId) {
      // Find by id
      var idx = data.findIndex(function (r) { return String(r.id) === String(reviewId); });
      if (idx < 0) return;
      renderReview(idx);
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      // 2026-05-11 (L24): lock the underlying page scroll while the dialog
      // is open. iOS Safari and some Android browsers let touch swipes on
      // a top-layer <dialog> bleed through to the page underneath — the
      // user reported "I slide on website as well." Storing the current
      // scrollY and re-applying it on close avoids the position:fixed
      // scroll-jump that body-locks usually cause.
      try {
        var sy = window.scrollY || window.pageYOffset || 0;
        document.body.dataset.communityModalScrollY = String(sy);
        document.body.classList.add('community-modal-open');
        document.body.style.top = '-' + sy + 'px';
      } catch (e) {}
      // 2026-05-11 (L19): first-time swipe hint on mobile. Briefly animate
      // the dialog body to suggest the swipe-to-navigate gesture. Sessions
      // see it once; the flag in sessionStorage clears on tab close.
      try {
        if (window.innerWidth < 768) {
          var hintKey = 'trieye_community_reviews_swipe_hinted';
          var seen = window.sessionStorage.getItem(hintKey) === '1';
          if (!seen) {
            window.sessionStorage.setItem(hintKey, '1');
            dialog.classList.add('community-reviews__dialog--swipe-hint');
            setTimeout(function () {
              dialog.classList.remove('community-reviews__dialog--swipe-hint');
            }, 1800);
          }
        }
      } catch (e) { /* sessionStorage blocked — silently skip the hint */ }
    }

    function closeModal() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      currentIdx = -1;
      // L24: restore page scroll after the body-lock above.
      try {
        var sy = parseInt(document.body.dataset.communityModalScrollY || '0', 10) || 0;
        document.body.classList.remove('community-modal-open');
        document.body.style.top = '';
        delete document.body.dataset.communityModalScrollY;
        window.scrollTo(0, sy);
      } catch (e) {}
    }

    // 2026-05-11 (L21): sliding-motion navigation. Instead of an
    // instant innerHTML swap, the dialog body slides out in the
    // direction of travel (left for "next", right for "prev"), then
    // the new review is rendered offscreen and slides in from the
    // opposite side. `animating` gates re-entry so rapid swipes don't
    // overlap. Total round-trip is 320ms — fast enough to feel
    // responsive, slow enough to read as motion.
    var animating = false;
    var SLIDE_OUT_MS = 160;
    var SLIDE_IN_MS = 220;
    function navigate(delta) {
      if (animating) return;
      var next = currentIdx + delta;
      if (next < 0 || next >= data.length) return;
      animating = true;
      var outClass = delta > 0
        ? 'community-reviews__dialog-body--slide-out-left'
        : 'community-reviews__dialog-body--slide-out-right';
      var inClass = delta > 0
        ? 'community-reviews__dialog-body--slide-in-right'
        : 'community-reviews__dialog-body--slide-in-left';
      dialogBody.classList.add(outClass);
      setTimeout(function () {
        renderReview(next);
        dialogBody.classList.remove(outClass);
        dialogBody.classList.add(inClass);
        // Force reflow so the in-class transition runs from its
        // initial offset state (without this, the browser collapses
        // the add+remove into a no-op and the slide-in is invisible).
        // eslint-disable-next-line no-unused-expressions
        dialogBody.offsetWidth;
        setTimeout(function () {
          dialogBody.classList.remove(inClass);
          animating = false;
        }, SLIDE_IN_MS);
      }, SLIDE_OUT_MS);
    }

    cards.forEach(function (card) {
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.addEventListener('click', function () { openModal(card.dataset.reviewId); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModal(card.dataset.reviewId);
        }
      });
    });

    // Close on backdrop click (but not on dialog content clicks)
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) closeModal();
    });
    var closeBtn = dialog.querySelector('.community-reviews__dialog-close');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (prevBtn) prevBtn.addEventListener('click', function () { navigate(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { navigate(1); });

    // Keyboard navigation while dialog is open
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
    });

    // 2026-05-11 (L19 + L24): touch swipe navigation. On mobile the
    // prev/next arrow buttons are CSS-hidden — swipe replaces them.
    //
    // L24 improvements: track touchmove with `passive: false` so we can
    // preventDefault on horizontal-dominant gestures. Without this, the
    // browser fires native scroll alongside the user's swipe — the user
    // reported "sometimes it doesn't navigate" because the browser had
    // already committed to scrolling and our touchend handler ran on a
    // changedTouches position that had drifted. Now once we detect a
    // horizontal-dominant intent we lock out the browser's scroll and
    // own the gesture through to touchend.
    var touchStartX = null, touchStartY = null;
    var touchLockedAxis = null;  // 'x' | 'y' | null
    var SWIPE_THRESHOLD = 40;    // lowered from 50 — swipe was sometimes ignored
    var AXIS_LOCK_THRESHOLD = 8; // detect horizontal vs vertical intent within 8px
    dialog.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchLockedAxis = null;
    }, { passive: true });
    dialog.addEventListener('touchmove', function (e) {
      if (touchStartX === null || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - touchStartX;
      var dy = e.touches[0].clientY - touchStartY;
      if (touchLockedAxis === null) {
        // Wait until either axis crosses the lock threshold, then decide.
        if (Math.abs(dx) < AXIS_LOCK_THRESHOLD && Math.abs(dy) < AXIS_LOCK_THRESHOLD) return;
        touchLockedAxis = (Math.abs(dx) > Math.abs(dy)) ? 'x' : 'y';
      }
      // Horizontal-dominant: own the gesture. Native scroll would compete
      // with our swipe nav otherwise.
      if (touchLockedAxis === 'x' && e.cancelable) e.preventDefault();
    }, { passive: false });
    dialog.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      var dy = e.changedTouches[0].clientY - touchStartY;
      touchStartX = touchStartY = null;
      var lockedAxis = touchLockedAxis;
      touchLockedAxis = null;
      if (lockedAxis === 'y') return;            // user was scrolling vertically
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) < Math.abs(dy)) return;
      if (dx > 0) navigate(-1);                  // swipe right → previous
      else navigate(1);                          // swipe left  → next
    }, { passive: true });
    dialog.addEventListener('touchcancel', function () {
      touchStartX = touchStartY = null;
      touchLockedAxis = null;
    }, { passive: true });

    // ===== L39: bfcache reset =====
    // Closes the modal, restores body scroll lock, and collapses
    // load-more state back to the initial cap. Triggered from the
    // page-level `pageshow` listener below when persisted === true.
    // We listen on the section so each instance owns its own state —
    // a page with multiple community-reviews sections (rare, but
    // possible) resets each independently.
    section.addEventListener('community-reviews:reset', function () {
      // 1. Close the modal if it's still open (bfcache restores
      //    <dialog open=""> state).
      if (dialog.open || dialog.hasAttribute('open')) {
        if (typeof dialog.close === 'function') {
          try { dialog.close(); } catch (e) { dialog.removeAttribute('open'); }
        } else {
          dialog.removeAttribute('open');
        }
      }
      currentIdx = -1;
      // 2. Restore page scroll lock state — see L24 lock logic above.
      try {
        document.body.classList.remove('community-modal-open');
        document.body.style.top = '';
        if (document.body.dataset.communityModalScrollY) {
          delete document.body.dataset.communityModalScrollY;
        }
      } catch (e) {}
      // 3. Reset Load more pagination. `initial` was captured as a
      //    number at the top of init() (line ~31); the inner media-
      //    toggle block shadows it with a boolean (line ~89), but by
      //    the time this handler runs we want the number again, so
      //    we re-read from the dataset to be safe.
      var initialFromDataset = parseInt(section.dataset.initialCount || '12', 10);
      visible = initialFromDataset;
      applyVisibility();
    });
  }

  function setupAll() {
    var sections = document.querySelectorAll('.community-reviews');
    sections.forEach(init);
  }

  // L39: bfcache restore handler. `event.persisted === true` means the
  // page was served from the back-forward cache — the DOM and our JS
  // closures are restored as-is. Dispatch the reset event so each
  // community-reviews section collapses to its initial state.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var sections = document.querySelectorAll('.community-reviews');
    sections.forEach(function (section) {
      // Skip empty-state and uninitialised sections — they have no
      // state to reset.
      if (section.dataset.communityInit !== '1') return;
      try {
        section.dispatchEvent(new CustomEvent('community-reviews:reset'));
      } catch (err) {
        // IE / old Safari without CustomEvent — fall back to plain
        // Event with detail-less semantics. (Pipeline supports
        // evergreen browsers, this is paranoia.)
        var fallback = document.createEvent('Event');
        fallback.initEvent('community-reviews:reset', true, true);
        section.dispatchEvent(fallback);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAll);
  } else {
    setupAll();
  }
})();
